import React from "react";
import {
  Dialog,
  FormGroup,
} from "@blueprintjs/core";
import { Formik } from "formik";
import * as Yup from "yup";
import { Maybe } from "common-types";

import StyledDialogContentWrapper from "../../sharedStyledComponents/styledDialogContentWrapper";
import StyledBtn from "../../sharedStyledComponents/styledBtn";
import {
  StyledFormControlsWrapper,
  StyledInput,
  StyledSelect
} from "./styles";
import { EditPlayer } from "../../containers/Layout";
import { 
  IPlayer, ITeam
} from "../../stateUtils";

interface IProps {
  editPlayer: EditPlayer;
  closeDialog: () => any;
  playerInfo: Maybe<IPlayer>;
  teams: ITeam[];
}

export default ({
  editPlayer,
  closeDialog,
  playerInfo,
  teams
}: IProps) => {
  return (
    <Dialog
      isOpen={!!playerInfo}
      title="Edit Player"
      onClose={() => playerInfo && closeDialog()}
      portalClassName="app-dialog"
    >
      <StyledDialogContentWrapper>
        <Formik
          initialValues={{
            firstName: playerInfo ? playerInfo.firstName : "",
            lastName: playerInfo ? playerInfo.lastName : "",
            coursePlayed: playerInfo ? playerInfo.coursePlayed : "",
            teamId: playerInfo ? playerInfo.teamId : "",
            handicap: playerInfo ? playerInfo.handicap : "",
            score: playerInfo ? playerInfo.score : "",
          }}
          onSubmit={values => {
            const { firstName, lastName, coursePlayed, teamId, score, handicap } = values;
            editPlayer(
              playerInfo!.id,
              {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                coursePlayed: coursePlayed.trim(),
                teamId: teamId,
                leaderboardId: playerInfo!.leaderboardId,
                handicap: parseInt(handicap as string, 10),
                score: parseInt(score as string, 10),
              }
            );

            closeDialog();
          }}
          validationSchema={Yup.object().shape({
            firstName: Yup.string()
              .trim()
              .max(100, "Must be less than 100 characters")
              .required("Please input player's first name."),
            lastName: Yup.string()
              .trim()
              .max(100, "Must be less than 100 characters")
              .required("Please input player's last name."),
            coursePlayed: Yup.string()
            .trim()
            .max(100, "Must be less than 100 characters")
            .required("Please input player's last name."),
            handicap: Yup.number()
              .min(0, "Must be a positive number")
              .max(100, "Must be less that 100")
              .required("Please input player's score."),
            score: Yup.number()
              .min(0, "Must be a positive number")
              .max(100, "Must be less that 100")
              .required("Please input player's score."),
          })}
          render={props => (
            <form onSubmit={props.handleSubmit} style={{ marginBottom: "0" }}>
              <FormGroup
                label="First Inital"
                labelFor="firstName"
                labelInfo="(Required)"
                intent={props.errors.firstName ? "danger" : undefined}
                helperText={
                  (props.errors.firstName && props.touched.firstName) ?
                    props.errors.firstName :
                    undefined
                }
              >
                <StyledInput
                  id="firstName"
                  placeholder="Title..."
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  value={props.values.firstName}
                />
              </FormGroup>

              <FormGroup
                label="Last Name"
                labelFor="lastName"
                labelInfo="(Required)"
                intent={props.errors.lastName ? "danger" : undefined}
                helperText={
                  (props.errors.lastName && props.touched.lastName) ?
                    props.errors.lastName :
                    undefined
                }
              >
                <StyledInput
                  id="lastName"
                  placeholder="Title..."
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  value={props.values.lastName}
                />
              </FormGroup>

              <FormGroup
                label="Course Played"
                labelFor="coursePlayed"
                labelInfo="(Required)"
                intent={props.errors.coursePlayed ? "danger" : undefined}
                helperText={
                  (props.errors.coursePlayed && props.touched.coursePlayed) ?
                    props.errors.coursePlayed :
                    undefined
                }
              >
                <StyledInput
                  id="coursePlayed"
                  placeholder="Title..."
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  value={props.values.coursePlayed}
                />
              </FormGroup>

              <FormGroup
                label="Team"
                labelFor="teamId"
                labelInfo="(Required)"
                intent={props.errors.teamId ? "danger" : undefined}
                helperText={
                  (props.errors.teamId && props.touched.teamId) ?
                    props.errors.teamId:
                    undefined
                }
              >
                <StyledSelect
                  id="teamId"
                  placeholder="Select Team"
                  onChange={selectedOption => {
                    props.values.teamId = selectedOption.id
                  }}
                  options={teams}
                  getOptionLabel={option => option.name}
                />

              </FormGroup>
              

              <FormGroup
                label="Handicap"
                labelFor="handicap"
                labelInfo="(Required)"
                intent={props.errors.handicap ? "danger" : undefined}
                helperText={
                  (props.errors.handicap && props.touched.handicap) ?
                    props.errors.handicap :
                    undefined
                }
              >
                <StyledInput
                  id="handicap"
                  placeholder="Handicap"
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  value={`${props.values.handicap}`}
                  type="number"
                  min={0}
                  max={100}
                  style={{
                    marginRight: "2%",
                    width: "14%",
                    minWidth: "14%",
                  }}
                />
              </FormGroup>

              <FormGroup
                label="Score"
                labelFor="score"
                labelInfo="(Required)"
                intent={props.errors.score ? "danger" : undefined}
                helperText={
                  (props.errors.score && props.touched.score) ?
                    props.errors.score :
                    undefined
                }
              >
                <StyledInput
                  id="score"
                  placeholder="Score (1-100)..."
                  onChange={props.handleChange}
                  onBlur={props.handleBlur}
                  value={`${props.values.score}`}
                  type="number"
                  min={0}
                  max={100}
                  style={{
                    marginRight: "2%",
                    width: "14%",
                    minWidth: "14%",
                  }}
                />
              </FormGroup>

              <StyledBtn
                type="submit"
                intent="primary"
                disabled={!!Object.entries(props.errors).length}
                isDisabled={!!Object.entries(props.errors).length}
                style={{
                  height: "40px",
                }}
              >
                Save
                </StyledBtn>
            </form>
          )}
        />
      </StyledDialogContentWrapper>
    </Dialog>
  );
};
